import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { api } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import WorkerFormDialog from "@/components/WorkerFormDialog";
import MapDialog from "@/components/MapDialog";
import WorkerDetailDialog from "@/components/WorkerDetailDialog";
import WorkersTable from "@/components/workers/WorkersTable";
import WorkersCardList from "@/components/workers/WorkersCardList";
import WorkersBulkStatusDialog from "@/components/workers/WorkersBulkStatusDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { sertifikatPekerja, adaSertifikatKritis, labelSertifikat } from "@/lib/sertifikat";
import { useWorkerFilters, USIA_RANGES, masaKerjaTahun } from "@/components/workers/useWorkerFilters";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { LOKASI_OPTIONS, STATUS_OPTIONS, LEVEL_OPTIONS } from "@/lib/constants";
import { Plus, Search, FileSpreadsheet, FileText, Loader2, Upload, Download, Printer, ClipboardEdit, Trash2 } from "lucide-react";

const unduhBlob = (data, mime, nama) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nama;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 2000);
};

const MIME_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export default function WorkersView() {
  const { user } = useAuth();
  const [workers, setWorkers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mapTarget, setMapTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [exporting, setExporting] = useState("");
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [usiaPensiun, setUsiaPensiun] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [printingBulk, setPrintingBulk] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [lokasiOptions, setLokasiOptions] = useState(LOKASI_OPTIONS);
  const fileInputRef = useRef(null);

  const load = useCallback(() => api.get("/workers").then((res) => setWorkers(res.data)), []);
  useEffect(() => {
    load();
    api.get("/settings").then((res) => setUsiaPensiun(res.data.usia_pensiun)).catch(() => {});
    api.get("/lokasi").then((res) => setLokasiOptions(res.data)).catch(() => {});
  }, [load]);

  const jabatanOptions = useMemo(() => {
    if (!workers) return [];
    return [...new Set(workers.map((w) => w.jabatan).filter(Boolean))].sort();
  }, [workers]);

  const lokasiFilterOptions = useMemo(() => {
    const scoped = (user?.lokasi_kerja || []).filter(Boolean);
    if (scoped.length) {
      return [...new Set(scoped)].sort((a, b) => a.localeCompare(b, "id"));
    }
    const set = new Set(lokasiOptions);
    (workers || []).forEach((w) => {
      const l = (w.lokasi_kerja || "").trim();
      if (l) set.add(l);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
  }, [user, lokasiOptions, workers]);

  const { f, set: setFilter, setF, filtered, hasFilters, reset: resetFilters } = useWorkerFilters(workers, usiaPensiun);
  const { search, lokasi: fLokasi, jabatan: fJabatan, level: fLevel, status: fStatus, sertifikat: fSertifikat, pertalife: fPertalife, usia: fUsia, masaKerja: fMasaKerja } = f;
  const [setSearch, setFJabatan, setFLevel, setFStatus, setFSertifikat, setFPertalife, setFUsia, setFMasaKerja] = ["search", "jabatan", "level", "status", "sertifikat", "pertalife", "usia", "masaKerja"].map(setFilter);
  const setFLokasi = (v) => setF((s) => ({ ...s, lokasi: typeof v === "function" ? v(s.lokasi) : v }));
  const sertifikatOptions = useMemo(() => [...new Set((workers || []).flatMap((w) => sertifikatPekerja(w).map(labelSertifikat)))].sort(), [workers]);
  const nKritis = useMemo(() => (workers || []).filter(adaSertifikatKritis).length, [workers]);
  const masaKerjaOpts = useMemo(() => [...new Set((workers || []).map(masaKerjaTahun).filter((v) => v != null))].sort((a, b) => a - b), [workers]);


  const handleSave = async (payload, photo) => {
    let workerId;
    if (editing) {
      await api.put(`/workers/${editing.id}`, payload);
      workerId = editing.id;
      toast.success("Data pekerja diperbarui");
    } else {
      const res = await api.post("/workers", payload);
      workerId = res.data.id;
      toast.success("Pekerja baru ditambahkan");
    }
    try {
      if (photo?.photoFile) {
        const fd = new FormData();
        fd.append("file", photo.photoFile);
        await api.post(`/workers/${workerId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else if (photo?.removePhoto) {
        await api.delete(`/workers/${workerId}/photo`);
      }
    } catch {
      toast.error("Data tersimpan, tetapi foto gagal diunggah");
    }
    try {
      if (photo?.statusDocFile) {
        const fd = new FormData();
        fd.append("file", photo.statusDocFile);
        await api.post(`/workers/${workerId}/dokumen?jenis=${encodeURIComponent(`Bukti ${payload.status}`)}`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
    } catch {
      toast.error("Data tersimpan, tetapi dokumen bukti gagal diunggah");
    }
    try {
      for (const s of photo?.sertifikatBaru || []) {
        const fd = new FormData();
        fd.append("file", s.file);
        await api.post(`/workers/${workerId}/dokumen?jenis=${encodeURIComponent(`Sertifikat: ${s.keterangan}`)}&berlaku_sampai=${encodeURIComponent(s.berlaku_sampai || "")}`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
      for (const [id, v] of Object.entries(photo?.berlakuEdit || {})) {
        if (!(photo?.hapusSertifikat || []).includes(id)) await api.put(`/workers/${workerId}/dokumen/${id}`, { berlaku_sampai: v || "" });
      }
      for (const id of photo?.hapusSertifikat || []) {
        await api.delete(`/workers/${workerId}/dokumen/${id}`);
      }
    } catch {
      toast.error("Data tersimpan, tetapi sebagian sertifikat gagal diproses");
    }
    setDialogOpen(false);
    setEditing(null);
    load();
  };

  const confirmDelete = async () => {
    await api.delete(`/workers/${deleteTarget.id}`);
    toast.success("Pekerja dihapus");
    setDeleteTarget(null);
    load();
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await api.post("/workers/bulk/delete", { worker_ids: [...selected] });
      toast.success(`${res.data.deleted} pekerja berhasil dihapus`);
      setBulkDeleteOpen(false);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Gagal menghapus pekerja");
    } finally {
      setBulkDeleting(false);
    }
  };

  const doExport = async (type) => {
    setExporting(type);
    try {
      // Selalu kirim id sesuai urutan & filter di layar agar unduhan identik dengan yang tampil
      const params = filtered.length > 0 ? { ids: filtered.map((w) => w.id).join(",") } : {};
      const res = await api.get(`/export/${type}`, { params, responseType: "blob" });
      const mime = type === "excel" ? MIME_XLSX : "application/pdf";
      unduhBlob(res.data, mime, type === "excel" ? "data_pekerja.xlsx" : "data_pekerja.pdf");
      toast.success(hasFilters ? `Ekspor ${type.toUpperCase()} berhasil (${filtered.length} pekerja sesuai filter)` : `Ekspor ${type.toUpperCase()} berhasil`);
    } catch {
      toast.error("Gagal mengekspor data");
    } finally {
      setExporting("");
    }
  };

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await api.get("/import/template", { responseType: "blob" });
      unduhBlob(res.data, MIME_XLSX, "template_pekerja.xlsx");
      toast.success("Template Excel berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh template");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/import/excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message || "Impor berhasil");
      if (res.data.errors?.length) toast.warning(`Baris ditolak (${res.data.ditolak}):\n${res.data.errors.slice(0, 6).join("\n")}${res.data.errors.length > 6 ? "\n…" : ""}`, { duration: 15000 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengimpor file");
    } finally {
      setImporting(false);
    }
  };

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (w) => { setEditing(w); setDialogOpen(true); };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = filtered.length > 0 && filtered.every((w) => selected.has(w.id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((w) => w.id)));

  const printBulkCards = async () => {
    setPrintingBulk(true);
    try {
      const res = await api.post("/workers/cards", { worker_ids: [...selected] }, { responseType: "blob" });
      unduhBlob(res.data, "application/pdf", "kartu_pekerja_massal.pdf");
      toast.success(`Kartu ${selected.size} pekerja berhasil dibuat`);
    } catch {
      toast.error("Gagal membuat kartu massal");
    } finally {
      setPrintingBulk(false);
    }
  };

  const readOnly = ["staf_lembur", "staf_cek_lembur"].includes(user?.role);
  const rowActions = {
    detail: setDetailTarget,
    map: setMapTarget,
    edit: openEdit,
    hapus: setDeleteTarget,
    readOnly,
  };

  return (
    <div className="space-y-5" data-testid="workers-view">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <div className="relative w-full lg:max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama, nomor, jabatan, lokasi..." className="pl-10 h-11"
            data-testid="worker-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.size > 0 && (
            <>
              <Button onClick={printBulkCards} disabled={printingBulk} data-testid="print-cards-button"
                className="gap-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md transition-colors">
                {printingBulk ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                Cetak Kartu ({selected.size})
              </Button>
              <Button onClick={() => setBulkStatusOpen(true)} data-testid="bulk-status-button" disabled={readOnly}
                className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors">
                <ClipboardEdit className="h-4 w-4" />
                Ubah Status ({selected.size})
              </Button>
              {!readOnly && <Button onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-delete-button"
                className="gap-2 bg-rose-600 hover:bg-rose-700 text-white rounded-md transition-colors">
                <Trash2 className="h-4 w-4" />
                Hapus ({selected.size})
              </Button>}
            </>
          )}
          <Button onClick={() => doExport("excel")} variant="outline" disabled={exporting === "excel"}
            data-testid="export-excel-button"
            className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors">
            {exporting === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Ekspor Excel{hasFilters && filtered.length > 0 ? ` (${filtered.length})` : ""}
          </Button>
          <Button onClick={() => doExport("pdf")} variant="outline" disabled={exporting === "pdf"}
            data-testid="export-pdf-button"
            className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50 rounded-md transition-colors">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            Ekspor PDF
          </Button>
          {!readOnly && <>
          <Button onClick={downloadTemplate} variant="outline" disabled={downloadingTemplate}
            data-testid="download-template-button"
            className="gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-md transition-colors">
            {downloadingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Unduh Template
          </Button>
          <input type="file" ref={fileInputRef} accept=".xlsx" onChange={doImport} className="hidden" data-testid="import-file-input" />
          <Button onClick={() => fileInputRef.current?.click()} variant="outline" disabled={importing}
            data-testid="import-excel-button"
            className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50 rounded-md transition-colors">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Impor Excel
          </Button>
          <Button onClick={openAdd} data-testid="add-worker-button"
            className="gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors">
            <Plus className="h-4 w-4" /> Tambah Pekerja
          </Button>
          </>}
          {readOnly && <span className="text-xs text-slate-400 self-center" data-testid="workers-readonly-note">Mode lihat saja</span>}
        </div>
      </div>

      {/* Quick filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="worker-filters">
        <div className="flex flex-wrap items-center gap-1.5 border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid="filter-lokasi">
          <span className="text-xs font-semibold text-slate-500">Lokasi:</span>
          {lokasiFilterOptions.map((o) => {
            const on = fLokasi.includes(o);
            return (
              <button key={o} type="button" data-testid={`filter-lokasi-${o}`}
                onClick={() => setFLokasi((s) => (on ? s.filter((x) => x !== o) : [...s, o]))}
                className={`h-7 px-3 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-300 text-slate-500 hover:text-slate-800"}`}>
                {o}
              </button>
            );
          })}
          <span className="text-xs font-bold text-blue-700 ml-1" data-testid="filter-lokasi-total">Total: {filtered.length} pekerja</span>
        </div>
        <Select value={fJabatan} onValueChange={setFJabatan}>
          <SelectTrigger className="h-10 w-full sm:w-48" data-testid="filter-jabatan"><SelectValue placeholder="Jabatan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Jabatan</SelectItem>
            {jabatanOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fLevel} onValueChange={setFLevel}>
          <SelectTrigger className="h-10 w-full sm:w-40" data-testid="filter-level"><SelectValue placeholder="Level" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Level</SelectItem>
            {LEVEL_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-10 w-full sm:w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fSertifikat} onValueChange={setFSertifikat}>
          <SelectTrigger className={`h-10 w-full sm:w-52 ${fSertifikat === "kritis" ? "border-rose-300 text-rose-700" : ""}`} data-testid="filter-sertifikat"><SelectValue placeholder="Sertifikasi" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Sertifikasi</SelectItem>
            <SelectItem value="ada">Memiliki Sertifikasi</SelectItem>
            <SelectItem value="tidak">Tanpa Sertifikasi</SelectItem>
            <SelectItem value="kritis" data-testid="filter-sertifikat-kritis">Habis / ≤ 90 hari{nKritis > 0 ? ` (${nKritis})` : ""}</SelectItem>
            {sertifikatOptions.map((o) => <SelectItem key={o} value={o}>Jenis: {o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fPertalife} onValueChange={setFPertalife}>
          <SelectTrigger className="h-10 w-full sm:w-44" data-testid="filter-pertalife"><SelectValue placeholder="Pertalife" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Pertalife</SelectItem>
            <SelectItem value="ada">Ada No. Pertalife</SelectItem>
            <SelectItem value="tidak">Belum Ada Pertalife</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fUsia} onValueChange={setFUsia}>
          <SelectTrigger className={`h-10 w-full sm:w-44 ${fUsia === "pensiun" ? "border-rose-300 text-rose-700" : ""}`} data-testid="filter-usia"><SelectValue placeholder="Usia" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Usia</SelectItem>
            {USIA_RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            {usiaPensiun && <SelectItem value="pensiun">Usia Pensiun (≥ {usiaPensiun})</SelectItem>}
            <SelectItem value="kosong">Usia Belum Diisi</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fMasaKerja} onValueChange={setFMasaKerja}>
          <SelectTrigger className="h-10 w-full sm:w-44" data-testid="filter-masa-kerja"><SelectValue placeholder="Masa Kerja" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Masa Kerja</SelectItem>
            {masaKerjaOpts.map((t) => <SelectItem key={t} value={String(t)} data-testid={`filter-masa-kerja-${t}`}>{t === 0 ? "< 1 tahun" : `${t} tahun`}</SelectItem>)}
            <SelectItem value="kosong">Tgl Masuk Belum Diisi</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" onClick={resetFilters} data-testid="reset-filters"
            className="h-10 text-slate-500 hover:text-rose-600">Reset</Button>
        )}
        <span className="text-sm text-slate-500 ml-auto" data-testid="filter-count">{filtered.length} pekerja</span>
      </div>

      <WorkersTable workers={workers} filtered={filtered} usiaPensiun={usiaPensiun}
        selected={selected} toggleSelect={toggleSelect}
        allSelected={allSelected} toggleSelectAll={toggleSelectAll} on={rowActions} />

      <WorkersCardList workers={workers} filtered={filtered} usiaPensiun={usiaPensiun} on={rowActions} />

      <WorkerFormDialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        worker={editing} onSave={handleSave} />

      <MapDialog open={!!mapTarget} onOpenChange={(v) => !v && setMapTarget(null)} worker={mapTarget} />

      <WorkerDetailDialog open={!!detailTarget} onOpenChange={(v) => !v && setDetailTarget(null)} readOnly={readOnly}
        worker={detailTarget} onEdit={(w) => { setDetailTarget(null); openEdit(w); }} />

      <WorkersBulkStatusDialog open={bulkStatusOpen} onOpenChange={setBulkStatusOpen}
        selected={selected} onSaved={() => { setSelected(new Set()); load(); }} />

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="bulk-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {selected.size} Pekerja Sekaligus?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda akan menghapus <strong>{selected.size} pekerja terpilih</strong> secara permanen.
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete} disabled={bulkDeleting}
              data-testid="bulk-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700">
              {bulkDeleting ? "Menghapus..." : `Hapus ${selected.size} Pekerja`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Pekerja?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda akan menghapus <strong>{deleteTarget?.nama}</strong>. Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm"
              className="bg-rose-600 hover:bg-rose-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
